import type {
  AccessPermissionItem,
  AccessPermissionItemRaw,
  Group,
  IpBlocklistItem,
  ThreatTypeItem,
  Time,
} from "@/_store/cloud-apps/access-permissions.module";
import type { EmailProxyDomain } from "@/_store/email-security/email-proxy.module";
import { i18n } from "@/plugins/i18n";
import {
  AccessPermissionLocationPanel,
  AccessPermissionType,
  Service,
} from "@/constants/cloud-apps";
import type { UserExceptionsFilterItem } from "@/_store/users-settings/user-exceptions-tab.module";

export function convertPayloadForBE(payload: AccessPermissionItem): AccessPermissionItemRaw {
  // Remap groups from `name` and `service` to `groupName` and `serviceName` respectively
  const groups: Group[] = payload.groups.map(({ name, service, ...group }) => {
    return {
      ...group,
      groupName: group.groupName || name!,
      serviceName: group.serviceName || service!,
    };
  });

  const allowedStartTime = payload.allowedStartTime.split(":");
  const allowedEndTime = payload.allowedEndTime.split(":");

  return {
    ...payload,
    groups: payload.userType === AccessPermissionType.SPECIFIC_GROUPS ? groups : [],
    users: payload.userType === AccessPermissionType.SPECIFIC_USERS ? payload.users : [],
    labels: payload.userType === AccessPermissionType.LABEL ? payload.labels : [],
    allowedStartTime: payload.allowedStartTime
      ? {
          hours: Number.parseInt(allowedStartTime[0]),
          minutes: Number.parseInt(allowedStartTime[1]),
        }
      : null,
    allowedEndTime: payload.allowedStartTime
      ? {
          hours: Number.parseInt(allowedEndTime[0]),
          minutes: Number.parseInt(allowedEndTime[1]),
        }
      : null,
  };
}

export function convertThreatTypesPayloadForBE(payload: ThreatTypeItem): ThreatTypeItem {
  // Remap groups from `name` and `service` to `groupName` and `serviceName` respectively
  const groups: Group[] = payload.groups.map(({ name, service, ...group }) => {
    return {
      ...group,
      groupName: group.groupName || name!,
      serviceName: group.serviceName || service!,
    };
  });

  return {
    ...payload,
    groups: payload.userType === AccessPermissionType.SPECIFIC_GROUPS ? groups : [],
    users: payload.userType === AccessPermissionType.SPECIFIC_USERS ? payload.users : [],
    labels: payload.userType === AccessPermissionType.LABEL ? payload.labels : [],
  };
}

export const createUsersFiltersWithProxyDomains = (
  domains: EmailProxyDomain[]
): (UserExceptionsFilterItem | { divider: true } | { subheader: string })[] => {
  const result: (UserExceptionsFilterItem | { divider: true } | { subheader: string })[] = [];
  if (domains.length) {
    const domainFilterItems: UserExceptionsFilterItem[] = domains.map(({ name }) => ({
      type: "domain",
      name,
    }));
    result.push(
      { subheader: i18n.global.t("usersSettings.filters.inboundGateway") },
      ...domainFilterItems,
      { divider: true }
    );
  }
  const servicesFilterItems: UserExceptionsFilterItem[] = [
    Service.BOX,
    Service.DROPBOX,
    Service.G_SUITE,
    Service.OFFICE_365,
    Service.SLACK,
    Service.SALES_FORCE,
  ].map((name) => ({
    name,
    type: "cloudService",
  }));

  result.push(
    { subheader: i18n.global.t("usersSettings.filters.cloudServices") },
    ...servicesFilterItems
  );

  return result;
};

export function normalizeIpBlocklist(data: IpBlocklistItem[]): AccessPermissionItem[] {
  return data.map((item) => ({
    id: item.id,
    userType: item.userType,
    groups: item.groups,
    users: item.users,
    labels: item.labels,
    serviceName: "all",
    type: "ip",
    allowedLocations: [],
    allowedIps: item.blockedIps,
    description: item.description,
    allowedDays: [],
    allowedStartTime: "",
    allowedEndTime: "",
    remediation: item.remediation,
    locationPanel: AccessPermissionLocationPanel.RESTRICTED,
  }));
}

export function convertIpBlocklistPayloadForBE(payload: AccessPermissionItem): IpBlocklistItem {
  const groups: Group[] = payload.groups.map(({ name, service, ...group }) => ({
    ...group,
    groupName: group.groupName || name!,
    serviceName: group.serviceName || service!,
  }));

  return {
    id: payload.id,
    userType: payload.userType,
    groups: payload.userType === AccessPermissionType.SPECIFIC_GROUPS ? groups : [],
    users: payload.userType === AccessPermissionType.SPECIFIC_USERS ? payload.users : [],
    labels: payload.userType === AccessPermissionType.LABEL ? payload.labels : [],
    blockedIps: payload.allowedIps,
    remediation: payload.remediation,
    description: payload.description,
  };
}

export function normalizePolicies(data: AccessPermissionItemRaw[]): AccessPermissionItem[] {
  // If time is empty just populate with empty string, same goes to days
  return data.map((policy) => {
    return {
      ...policy,
      allowedStartTime: policy.allowedStartTime ? formatTime(policy.allowedStartTime) : "",
      allowedEndTime: policy.allowedEndTime ? formatTime(policy.allowedEndTime) : "",
    };
  });
}

function formatTime({ hours, minutes }: Time): string {
  const hh = hours.toString().padStart(2, "0");
  const mm = minutes.toString().padStart(2, "0");
  return `${hh}:${mm}`;
}
